export interface GeopositSuggestion {
  street?: string;
  street_number?: string;
  letter?: string;
  postcode?: string;
  locality?: string;
  adm1_code?: string;
  adm1_name?: string;
  adm2_code?: string;
  adm2_name?: string;
}

export interface GroupedResult {
  street: string;
  from: string;
  to: string;
  postcode: string;
  locality: string;
  lkfCode: string;
  county: string;
  municipality: string;
}

export interface AddressSuggestionItem {
  street: string;
  postcode: string;
  locality: string;
  streetNumber?: string;
  letter?: string;
}

function numericValue(numStr: string): number {
  const match = numStr.match(/^(\d+)/);
  return match ? parseInt(match[1], 10) : 0;
}

function numericOnly(numStr: string): string {
  const match = numStr.match(/^(\d+)/);
  return match ? match[1] : numStr;
}

export function groupSuggestions(suggestions: GeopositSuggestion[]): GroupedResult[] {
  const groups = new Map<string, {
    street: string;
    numbers: number[];
    postcode: string;
    locality: string;
    admLevel1Code: string;
    admLevel1Name: string;
    admLevel2Code: string;
    admLevel2Name: string;
  }>();

  for (const s of suggestions) {
    const street = s.street || '';
    const postcode = s.postcode || '';
    const locality = s.locality || '';
    const key = `${street}|${postcode}|${locality}`;
    const num = s.street_number ? parseInt(s.street_number, 10) : NaN;

    if (!groups.has(key)) {
      groups.set(key, {
        street,
        numbers: [],
        postcode,
        locality,
        admLevel1Code: s.adm1_code || '',
        admLevel1Name: s.adm1_name || '',
        admLevel2Code: s.adm2_code || '',
        admLevel2Name: s.adm2_name || '',
      });
    }

    if (!isNaN(num) && !groups.get(key)!.numbers.includes(num)) {
      groups.get(key)!.numbers.push(num);
    }
  }

  const results: GroupedResult[] = [];

  for (const group of groups.values()) {
    let from = '';
    let to = '';

    if (group.numbers.length > 0) {
      const sorted = [...group.numbers].sort((a, b) => a - b);
      from = String(sorted[0]);
      to = sorted.length > 1 ? String(sorted[sorted.length - 1]) : '';
    }

    const lkfCode = group.admLevel1Code && group.admLevel2Code
      ? `${group.admLevel1Code}${group.admLevel2Code}`
      : '';

    results.push({
      street: group.street,
      from,
      to,
      postcode: group.postcode,
      locality: group.locality,
      lkfCode,
      county: group.admLevel1Name,
      municipality: group.admLevel2Name,
    });
  }

  return results;
}

export function extractUniqueSuggestions(suggestions: GeopositSuggestion[]): AddressSuggestionItem[] {
  const seen = new Set<string>();
  const items: AddressSuggestionItem[] = [];

  for (const s of suggestions) {
    const street = s.street || '';
    const postcode = s.postcode || '';
    const locality = s.locality || '';
    const key = `${street}|${postcode}|${locality}`;

    if (!seen.has(key) && street) {
      seen.add(key);
      items.push({ street, postcode, locality });
    }
  }

  return items;
}
